/**
 * Centralised app-store link configuration.
 *
 * ⚠️  ATTENTION TO NEXT AGENT  ⚠️
 * --------------------------------------------------------------
 * The Allure Android app (com.allurexp.app) is currently in
 * Google Play Console **internal testing**. Until it is published
 * to the production track, the public Play Store URL will return
 * "item not found" for non-testers.
 *
 * To unblock real-world invite flows during this stage we expose
 * the INTERNAL TEST URL as the "active" Play Store link by default.
 *
 * 🟢 ACTION REQUIRED WHEN APP GOES LIVE ON PRODUCTION TRACK:
 *   - Flip `USE_INTERNAL_TESTING_LINK` below to `false`.
 *   - That single change swaps every invite landing page over to
 *     the public Play Store listing automatically.
 *
 * No other code needs to change — every page imports from here.
 * --------------------------------------------------------------
 */

export const ANDROID_PACKAGE_NAME = 'com.allurexp.app';

export const PLAY_STORE_PUBLIC_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE_NAME}`;
export const PLAY_STORE_INTERNAL_TESTING_URL = 'https://play.google.com/apps/internaltest/4700815185025683253';

// ⚠️  Flip to `false` once the app is on the production track on Google Play.
//     The Allure app is now visible on the public Play Store listing, so
//     testers land on the standard details page (which honours their tester
//     enrolment automatically) instead of the private internal-testing URL.
const USE_INTERNAL_TESTING_LINK = false;

export const PLAY_STORE_URL = USE_INTERNAL_TESTING_LINK
  ? PLAY_STORE_INTERNAL_TESTING_URL
  : PLAY_STORE_PUBLIC_URL;

// Apple App Store is not live yet; placeholder for when iOS build ships.
export const APP_STORE_URL = '';

/**
 * Deferred deep link via the Play Store `referrer` parameter.
 * Google Play persists the referrer string through install; on first launch
 * the app reads it back via the Install Referrer API (see the local
 * InstallReferrerPlugin + nativeDeepLinks.captureNativeLaunchContext) and
 * routes the user straight to the invite they originally tapped.
 *
 * Encoding: the referrer VALUE must itself be URL-encoded once, and the
 * deep-link path inside it once more, so Play's single decode leaves
 * `allure_dl=%2Finvite%2FAB12-CD34` intact for URLSearchParams to parse.
 */
export const buildPlayStoreDeferredUrl = (deepLinkPath) => {
  if (!deepLinkPath) return PLAY_STORE_URL;
  const referrer = encodeURIComponent(`allure_dl=${encodeURIComponent(deepLinkPath)}`);
  const joiner = PLAY_STORE_URL.includes('?') ? '&' : '?';
  return `${PLAY_STORE_URL}${joiner}referrer=${referrer}`;
};

export const IS_INTERNAL_TESTING_STAGE = USE_INTERNAL_TESTING_LINK;
